import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Loader2, Lock, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";
import Logo from "../components/Logo";
import { validateStrongPassword, pickApiError } from "../utils/authValidators";

/**
 * PasswordReset — Iteration 64
 *
 * Consumes `?token=<raw>&email=<email>` from the password-reset email and
 * lets the user pick a new password. Calls
 * POST /api/auth/password-reset/confirm. On success, redirects to the
 * right login page (B2B vs B2C) based on the `b2b=1` query flag.
 */
export default function PasswordReset() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = params.get("token") || "";
  const email = (params.get("email") || "").toLowerCase();
  const isB2B = params.get("b2b") === "1";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErr, setFieldErr] = useState({ pw1: "", pw2: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      setErr("This reset link is incomplete. Please request a new one from the sign-in page.");
    }
  }, [token, email]);

  const validate = () => {
    const e1 = validateStrongPassword(pw1) || "";
    const e2 = !pw2 ? "Confirm your new password" : pw1 !== pw2 ? "Passwords don't match" : "";
    setFieldErr({ pw1: e1, pw2: e2 });
    return !e1 && !e2;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", {
        token, email, new_password: pw1,
      });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => {
        navigate(isB2B ? "/b2b/login" : "/login", { replace: true });
      }, 1600);
    } catch (e1) {
      setErr(pickApiError(e1, "Could not reset your password. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const back = isB2B ? "/b2b/login" : "/login";

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
      data-testid="password-reset-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to={isB2B ? "/b2b" : "/"} className="inline-block">
            <Logo size="md" />
          </Link>
          <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider mt-4">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure password reset
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-7">
          {done ? (
            <div className="text-center py-4 space-y-3" data-testid="password-reset-success">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
              <h1 className="text-xl font-bold text-slate-900">Password updated</h1>
              <p className="text-sm text-slate-500">
                Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-slate-900">Set a new password</h1>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                {email ? (
                  <>For <span className="font-medium text-slate-700">{email}</span>. </>
                ) : null}
                Choose something at least 8 characters with a mix of cases and a number.
              </p>

              {err && (
                <div
                  className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5 rounded-xl mb-4"
                  data-testid="password-reset-error"
                  role="alert"
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                {/* Hidden email so password managers can save the new credential */}
                <input type="email" name="email" autoComplete="username" defaultValue={email} hidden readOnly />

                <PasswordField
                  id="pw1"
                  label="New password"
                  name="new-password"
                  autoComplete="new-password"
                  value={pw1}
                  onChange={setPw1}
                  show={show}
                  setShow={setShow}
                  error={fieldErr.pw1}
                  testid="password-reset-new"
                />
                <PasswordField
                  id="pw2"
                  label="Confirm new password"
                  name="confirm-password"
                  autoComplete="new-password"
                  value={pw2}
                  onChange={setPw2}
                  show={show}
                  setShow={setShow}
                  error={fieldErr.pw2}
                  testid="password-reset-confirm"
                />

                <Button
                  type="submit"
                  disabled={submitting || !token || !email}
                  className="w-full bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-12 text-sm font-semibold"
                  data-testid="password-reset-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                </Button>
              </form>

              <p className="text-xs text-slate-400 text-center mt-5">
                Back to{" "}
                <Link
                  to={back}
                  className="text-[#FF6B00] font-semibold hover:underline"
                  data-testid="password-reset-back-link"
                >
                  sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordField({ id, label, name, autoComplete, value, onChange, show, setShow, error, testid }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="relative">
        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          id={id}
          type={show ? "text" : "password"}
          name={name}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`rounded-xl h-11 pl-10 pr-10 border-slate-200 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${
            error ? "border-red-300 focus:border-red-400" : ""
          }`}
          required
          data-testid={`${testid}-input`}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          data-testid={`${testid}-show-btn`}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-600" data-testid={`${testid}-error`}>{error}</p>
      )}
    </div>
  );
}
